import { useState } from 'react';

interface AIMatchModalProps {
  onClose: () => void;
  currentUser: { name: string; role: string } | null;
  onOpenAuth: () => void;
}

interface MatchProfile {
  id: string;
  name: string;
  role: string;
  region: string;
  grade: string;
  subjects: string[];
  bio: string;
  avatar: string;
  matchScore: number;
  contact: string;
  wechat?: string;
  email?: string;
}

const mockMatches: MatchProfile[] = [
  {
    id: 'm1',
    name: '苗翠花',
    role: 'student',
    region: '云南昭通（农村）',
    grade: '高中二年级',
    subjects: ['英语', '语文'],
    bio: '来自云南山区，英语想提升，也想学习城市同学的表达方式，欢迎加我！',
    avatar: 'https://public.readdy.ai/ai/img_res/27f9d8c5040deb7d5b49b56a204f88ae.jpg',
    matchScore: 96,
    contact: '微信/邮箱',
    wechat: 'miaocuihua2008',
    email: 'cuihua@example.com',
  },
  {
    id: 'm2',
    name: '李秀英',
    role: 'student',
    region: '甘肃定西（农村）',
    grade: '高中二年级',
    subjects: ['语文', '历史'],
    bio: '甘肃农村，语文历史不错，想找城市同学帮我提升数学和英语，一起加油！',
    avatar: 'https://readdy.ai/api/search-image?query=young%20Chinese%20teenage%20girl%20student%20Gansu%20rural%20area%20simple%20dress%20genuine%20smile%20clean%20background%20portrait&width=80&height=80&seq=match002&orientation=squarish',
    matchScore: 91,
    contact: '邮箱',
    email: 'lixiuying@example.com',
  },
  {
    id: 'm3',
    name: '王建国老师',
    role: 'teacher',
    region: '北京',
    grade: '高中数学',
    subjects: ['数学', '高考冲刺'],
    bio: '北京四中数学老师，15年教学经验，愿意免费帮助农村学生提升数学成绩。',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20middle-aged%20male%20math%20teacher%20portrait%2C%20professional%20look%2C%20glasses%2C%20warm%20smile%2C%20clean%20background&width=80&height=80&seq=match003&orientation=squarish',
    matchScore: 88,
    contact: '微信',
    wechat: 'wangjg_teacher',
  },
];

type Step = 'form' | 'matching' | 'results';

export default function AIMatchModal({ onClose, currentUser, onOpenAuth }: AIMatchModalProps) {
  const [step, setStep] = useState<Step>('form');
  const [form, setForm] = useState({
    lookingFor: [] as string[],
    subjects: [] as string[],
    grade: '',
    region: '',
    description: '',
    uploadedDoc: '',
  });
  const [matches, setMatches] = useState<MatchProfile[]>([]);
  const [contactedIds, setContactedIds] = useState<Set<string>>(new Set());
  const [shownContact, setShownContact] = useState<string | null>(null);

  const lookingForOptions = ['学生学伴', '教师辅导', '家长交流', '考研伙伴'];
  const subjectOptions = ['数学', '英语', '语文', '物理', '化学', '生物', '历史', '政治', '考研'];

  const toggleOption = (arr: string[], val: string) =>
    arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];

  const handleMatch = () => {
    if (!currentUser) { onOpenAuth(); return; }
    setStep('matching');
    setTimeout(() => {
      setMatches(mockMatches);
      setStep('results');
    }, 2500);
  };

  const handleContact = (id: string) => {
    setContactedIds((prev) => { const n = new Set(prev); n.add(id); return n; });
    setShownContact(id);
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4 text-white flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-black text-lg flex items-center gap-2">
              <i className="ri-robot-2-line"></i> AI 智能匹配
            </h2>
            <p className="text-white/80 text-xs mt-0.5">告诉我你的需求，AI帮你找到最合适的伙伴</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 cursor-pointer">
            <i className="ri-close-line text-white"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 'form' && (
            <div className="flex flex-col gap-5">
              {/* Looking For */}
              <div>
                <label className="text-sm font-bold text-gray-800 mb-2 block">我在寻找</label>
                <div className="flex flex-wrap gap-2">
                  {lookingForOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setForm({ ...form, lookingFor: toggleOption(form.lookingFor, opt) })}
                      className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${
                        form.lookingFor.includes(opt) ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subjects */}
              <div>
                <label className="text-sm font-bold text-gray-800 mb-2 block">相关科目</label>
                <div className="flex flex-wrap gap-2">
                  {subjectOptions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm({ ...form, subjects: toggleOption(form.subjects, s) })}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                        form.subjects.includes(s) ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Grade */}
              <div>
                <label className="text-sm font-bold text-gray-800 mb-2 block">我的年级</label>
                <select
                  value={form.grade}
                  onChange={(e) => setForm({ ...form, grade: e.target.value })}
                  className="w-full bg-gray-50 rounded-xl px-4 py-2.5 text-sm outline-none cursor-pointer"
                >
                  <option value="">请选择年级</option>
                  {['小学', '初中', '高中', '大学', '考研备考'].map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>

              {/* Upload Document */}
              <div>
                <label className="text-sm font-bold text-gray-800 mb-2 block">
                  上传文档（可选）
                  <span className="text-gray-400 font-normal text-xs ml-2">上传你的学习资料或需求说明，AI更精准匹配</span>
                </label>
                <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 text-center hover:border-green-300 transition-colors cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-2">
                    <i className="ri-file-upload-line text-green-600 text-lg"></i>
                  </div>
                  <p className="text-sm text-gray-500">点击上传文档</p>
                  <p className="text-xs text-gray-400 mt-1">支持 PDF、Word、图片格式</p>
                  {form.uploadedDoc && (
                    <div className="mt-2 flex items-center justify-center gap-1 text-green-600 text-xs">
                      <i className="ri-check-line"></i> {form.uploadedDoc}
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-bold text-gray-800 mb-2 block">补充说明</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="描述你的具体需求，比如：我是农村高三学生，数学基础薄弱，希望找到有耐心的城市学长帮我讲解..."
                  rows={3}
                  maxLength={200}
                  className="w-full bg-gray-50 rounded-xl px-4 py-3 text-sm outline-none resize-none"
                />
                <p className="text-right text-xs text-gray-400 mt-1">{form.description.length}/200</p>
              </div>

              <button
                onClick={handleMatch}
                className="w-full py-3.5 bg-green-600 text-white font-bold rounded-full cursor-pointer hover:bg-green-700 transition-all whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-robot-2-line"></i>
                开始AI智能匹配
              </button>
            </div>
          )}

          {step === 'matching' && (
            <div className="flex flex-col items-center justify-center py-16 gap-6">
              <div className="relative w-20 h-20">
                <div className="absolute inset-0 rounded-full border-4 border-green-100"></div>
                <div className="absolute inset-0 rounded-full border-4 border-green-500 border-t-transparent animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <i className="ri-robot-2-line text-green-600 text-2xl"></i>
                </div>
              </div>
              <div className="text-center">
                <p className="font-bold text-gray-900 text-lg mb-2">AI 正在分析匹配中...</p>
                <p className="text-gray-500 text-sm">正在从 12,847 名用户中为你寻找最合适的伙伴</p>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div className="h-full bg-green-500 rounded-full animate-pulse" style={{ width: '70%' }}></div>
              </div>
              <div className="flex flex-col gap-2 w-full">
                {['分析你的学习需求...', '匹配年级和科目...', '评估互补性...'].map((text, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-500">
                    <i className="ri-check-line text-green-500"></i> {text}
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'results' && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2 bg-green-50 rounded-2xl p-4">
                <i className="ri-robot-2-line text-green-600 text-xl"></i>
                <div>
                  <p className="font-bold text-green-800 text-sm">AI 匹配完成！</p>
                  <p className="text-green-600 text-xs">为你找到 {matches.length} 位高度匹配的伙伴</p>
                </div>
              </div>

              {matches.map((match) => (
                <div key={match.id} className="bg-white border border-gray-100 rounded-2xl p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <img src={match.avatar} alt={match.name} className="w-12 h-12 rounded-full object-cover object-top flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-gray-900 text-sm">{match.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          match.role === 'student' ? 'bg-blue-100 text-blue-700' :
                          match.role === 'teacher' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {match.role === 'student' ? '学生' : match.role === 'teacher' ? '教师' : '家长'}
                        </span>
                        <span className="ml-auto text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                          匹配度 {match.matchScore}%
                        </span>
                      </div>
                      <p className="text-gray-500 text-xs mt-0.5">{match.region} · {match.grade}</p>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {match.subjects.map((s) => (
                          <span key={s} className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-xs leading-relaxed mb-3">{match.bio}</p>

                  {shownContact === match.id ? (
                    <div className="bg-green-50 rounded-xl p-3 text-sm">
                      <p className="font-semibold text-green-800 mb-2 text-xs">联系方式</p>
                      {match.wechat && (
                        <div className="flex items-center gap-2 text-xs text-gray-700 mb-1">
                          <i className="ri-wechat-line text-green-600"></i>
                          <span>微信：<strong>{match.wechat}</strong></span>
                        </div>
                      )}
                      {match.email && (
                        <div className="flex items-center gap-2 text-xs text-gray-700">
                          <i className="ri-mail-line text-blue-500"></i>
                          <span>邮箱：<strong>{match.email}</strong></span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => handleContact(match.id)}
                      className="w-full py-2 bg-green-600 text-white text-xs font-semibold rounded-full cursor-pointer hover:bg-green-700 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-exchange-line mr-1"></i>
                      交换联系方式（微信/邮箱）
                    </button>
                  )}
                </div>
              ))}

              <button
                onClick={() => setStep('form')}
                className="w-full py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-full cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                重新匹配
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
